import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Row, Col, Select, DatePicker, Radio, Space, Button, Typography, message } from 'antd';
import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import moment from 'moment';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, ResponsiveContainer
} from 'recharts';

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const API_BASE_URL = (process.env.REACT_APP_UTIL_URL || '').replace(/\/$/, '');
const allowedRanges = ['daily', 'weekly', 'monthly', 'yearly'];

const SignupDetails = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [filterType, setFilterType] = useState('range');
  const [selectedRange, setSelectedRange] = useState('weekly');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Table columns
  const columns = useMemo(() => [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: text => text ? moment(text).format('YYYY-MM-DD') : '-',
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
      defaultSortOrder: 'descend'
    },
    {
      title: 'Workshop Signups',
      dataIndex: 'workshopSignup',
      key: 'workshopSignup',
      render: val => <Text strong>{val || 0}</Text>,
      sorter: (a, b) => (a.workshopSignup || 0) - (b.workshopSignup || 0)
    },
    {
      title: 'English Signups',
      dataIndex: 'englishSignup',
      key: 'englishSignup',
      render: val => <Text strong>{val || 0}</Text>,
      sorter: (a, b) => (a.englishSignup || 0) - (b.englishSignup || 0)
    },
    {
      title: 'Total Signups',
      key: 'total',
      render: record => <Text strong>{(record.workshopSignup || 0) + (record.englishSignup || 0)}</Text>,
      sorter: (a, b) => ((a.workshopSignup || 0) + (a.englishSignup || 0)) - ((b.workshopSignup || 0) + (b.englishSignup || 0))
    }
  ], []);

  // Fetch data from API
  const fetchSignupData = useCallback(async () => {
    setLoading(true);
    try {
      const params = filterType === 'range'
        ? { range: selectedRange }
        : { startDate, endDate };
      const res = await axios.get(`${API_BASE_URL}/api/report/total-signup-summary`, { params });
      if (res.data.success) {
        let fetchedData = res.data.data;
        if (fetchedData && !Array.isArray(fetchedData)) {
          fetchedData = [{
            date: filterType === 'date' ? startDate || moment().format('YYYY-MM-DD') : null,
            workshopSignup: fetchedData.workshopSignup,
            englishSignup: fetchedData.englishSignup
          }];
        }

        // Transform + sort (latest date first)
        const chartData = (fetchedData || [])
          .map(item => ({
            date: moment(item.date, 'DD-MM-YYYY').format('DD-MM-YYYY'),
            workshopSignup: item.workshopSignup || 0,
            englishSignup: item.englishSignup || 0,
            total: (item.workshopSignup || 0) + (item.englishSignup || 0),
          }))
          .sort((a, b) => moment(b.date, 'DD-MM-YYYY') - moment(a.date, 'DD-MM-YYYY'));

        setData(chartData);
        message.success('Data fetched successfully');
      } else {
        message.error('Failed to fetch data');
      }
    } catch (err) {
      console.error(err);
      message.error('Error fetching data');
    } finally {
      setLoading(false);
    }
  }, [filterType, selectedRange, startDate, endDate]);

  // Handlers
  const handleRangeChange = value => { setSelectedRange(value); setFilterType('range'); };
  const handleDateRangeChange = dates => {
    if (dates?.length === 2) {
      // Send API in YYYY-MM-DD format
      setStartDate(dates[0].format('YYYY-MM-DD'));
      setEndDate(dates[1].format('YYYY-MM-DD'));
      setFilterType('date');
    } else { setStartDate(''); setEndDate(''); }
  };
  const handleFilterTypeChange = e => {
    const value = e.target.value;
    setFilterType(value);
    if (value === 'range') { setStartDate(''); setEndDate(''); }
    else {
      const today = moment().format('YYYY-MM-DD');
      setStartDate(today); setEndDate(today);
    }
  };

  // CSV Export
  const exportToCSV = useCallback(() => {
    if (!data.length) return message.warning('No data to export');
    const headers = ['Date', 'Workshop Signups', 'English Signups', 'Total Signups'];
    const csvRows = data.map(item => [
      moment(item.date).format('YYYY-MM-DD'),
      item.workshopSignup || 0,
      item.englishSignup || 0,
      (item.workshopSignup || 0) + (item.englishSignup || 0)
    ]);
    const csvContent = [headers, ...csvRows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `signup-details-${moment().format('YYYY-MM-DD')}.csv`;
    link.click();
    message.success('CSV exported successfully');
  }, [data]);

  useEffect(() => { fetchSignupData(); }, [fetchSignupData]);

  // Highlight latest date
  const latestDate = useMemo(() => {
    if (!data.length) return null;
    return data.reduce((a, b) => moment(a.date, 'YYYY-MM-DD').isAfter(moment(b.date, 'YYYY-MM-DD')) ? a : b).date;
  }, [data]);

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <Card
        title={<Text strong style={{ fontSize: 18 }}>Signup Counts</Text>}
        bordered={false}
        style={{ borderRadius: 12, boxShadow: '0 6px 20px rgba(0,0,0,0.08)' }}
        extra={
          <Space wrap>
            <Button icon={<ReloadOutlined />} onClick={fetchSignupData} loading={loading}>Refresh</Button>
            <Button type="primary" icon={<DownloadOutlined />} onClick={exportToCSV} disabled={!data.length}>Export CSV</Button>
          </Space>
        }
      >
        {/* Filters */}
<Row gutter={16} align="middle" style={{ marginBottom: 24 }}>
  {/* Filter Type */}
  <Col xs={24} sm={8}>
    <Space direction="horizontal" align="center">
      <Text strong style={{ minWidth: 100 }}>Filter Type:</Text>
      <Radio.Group 
        value={filterType} 
        onChange={handleFilterTypeChange} 
        buttonStyle="solid"
      >
        <Radio.Button value="range">Range</Radio.Button>
        <Radio.Button value="date">Custom Date</Radio.Button>
      </Radio.Group>
    </Space>
  </Col>

  {/* Range or Date Picker */}
  <Col xs={24} sm={8}>
    <Space direction="horizontal" align="center">
      {filterType === 'range' ? (
        <>
          <Text strong style={{ minWidth: 100 }}>Select Range:</Text>
          <Select 
            value={selectedRange} 
            onChange={handleRangeChange} 
            style={{ width: 150 }}
          >
            {allowedRanges.map(r => (
              <Option key={r} value={r}>
                {r.charAt(0).toUpperCase() + r.slice(1)}
              </Option>
            ))}
          </Select>
        </>
      ) : (
        <>
          <Text strong style={{ minWidth: 140}}>Date Range:</Text>
          <RangePicker
            value={startDate && endDate ? [moment(startDate, 'YYYY-MM-DD'), moment(endDate, 'YYYY-MM-DD')] : null}
            onChange={handleDateRangeChange}
            format="YYYY-MM-DD"
          />
        </>
      )}
    </Space>
  </Col>
</Row>



        {/* Generate Report Button */}
        <Button type="primary" size="large" block onClick={fetchSignupData} loading={loading} style={{ marginBottom: 24 }}>
          Generate Report
        </Button>

        {/* Bar Chart */}
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="workshopSignup" fill="#1890ff" name="Workshop Signups" />
            <Bar dataKey="englishSignup" fill="#52c41a" name="English Signups" />
            <Bar dataKey="total" fill="#722ed1" name="Total Signups" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      <style>{`
        .highlight-latest-row {
          background-color: #e6f7ff !important;
          font-weight: 600;
        }
        @media (max-width: 768px) {
          .ant-table-cell {
            font-size: 13px;
          }
          .ant-btn {
            font-size: 13px;
          }
        }
      `}</style>
    </div>
  );
};

export default SignupDetails;
